import { useHasActivePanel } from "@/hooks/useHasActivePanel";
import { useMentoringWallet, type MentoringWallet } from "@/hooks/useMentoringWallet";
import { useMentoringCancellation } from "@/hooks/useMentoringCancellation";

export type ExpertSupportState = "lite" | "hours" | "panel" | "vip";

export interface ExpertSupportSnapshot {
  state: ExpertSupportState;
  hasPanel: boolean;
  isAdmin: boolean;
  wallet: MentoringWallet | null;
  refetchWallet: () => void;
  loading: boolean;
  /** Cancellation date if mentoring is ending soon, null otherwise */
  cancellationDate: Date | null;
}

/**
 * Single source of truth for the "Apoyo Experto" module renderer.
 *
 * Maps user types to existing BD tables (no duplicated columns in profiles):
 *  - lite  → no active Panel, no hours, no mentoring plan
 *  - hours → no mentoring plan but has usable hours (extra_hours_credits or
 *            mentoring_promo_credits). Applies with or without Panel — the
 *            user has real hours to book with, even without a subscription.
 *  - panel → active Panel (paid or courtesy / admin), no plan, no hours
 *  - vip   → active client_plans (mentoring add-on) — regardless of cancelled flag
 */
export function useExpertSupportState(): ExpertSupportSnapshot {
  const { hasPanel, isAdmin, loading: panelLoading } = useHasActivePanel();
  const { wallet, loading: walletLoading, refetch: refetchWallet } = useMentoringWallet();
  const { cancellationDate } = useMentoringCancellation();

  const loading = panelLoading || walletLoading;
  const hasMentoring = !!wallet?.hasPlan;
  const hasHours = !hasMentoring && (wallet?.totalRemaining ?? 0) > 0;

  let state: ExpertSupportState = "lite";
  if (hasMentoring) state = "vip";
  else if (hasHours) state = "hours";
  else if (hasPanel) state = "panel";

  return {
    state,
    hasPanel,
    isAdmin,
    wallet,
    refetchWallet,
    loading,
    cancellationDate: cancellationDate ?? null,
  };
}
